/**
 * Calcula el offset en horas entre una timezone y UTC
 */
function getTimezoneOffsetHours(timezone: string): number {
  const today = new Date();
  const year = today.getUTCFullYear();
  const month = today.getUTCMonth();
  const day = today.getUTCDate();

  const testDate = new Date(Date.UTC(year, month, day, 12, 0, 0));

  const tzFormatter = new Intl.DateTimeFormat('en-US', {
    timeZone: timezone,
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });

  const utcFormatter = new Intl.DateTimeFormat('en-US', {
    timeZone: 'UTC',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });

  const tzTime = tzFormatter.format(testDate);
  const utcTime = utcFormatter.format(testDate);

  const [tzHour] = tzTime.split(':').map(Number);
  const [utcHour] = utcTime.split(':').map(Number);

  let offsetHours = tzHour - utcHour;
  if (offsetHours > 12) offsetHours -= 24;
  if (offsetHours < -12) offsetHours += 24;

  return offsetHours;
}

export function localTimeToUTC(timeStr: string, timezone: string): string {
  return localTimeToUTCWithDayShift(timeStr, timezone).time;
}

/**
 * Convierte hora local a UTC y retorna cuántos días cambia (0, +1, -1)
 */
export function localTimeToUTCWithDayShift(timeStr: string, timezone: string): { time: string; dayShift: number } {
  const [hours, minutes, seconds = '0'] = timeStr.split(':').map(Number);

  const offsetHours = getTimezoneOffsetHours(timezone);

  let utc_hour = hours - offsetHours;

  let dayShift = 0;
  if (utc_hour < 0) { utc_hour += 24; dayShift = -1; }
  if (utc_hour >= 24) { utc_hour -= 24; dayShift = 1; }

  const hh = String(utc_hour).padStart(2, '0');
  const mm = String(minutes).padStart(2, '0');
  const ss = String(seconds).padStart(2, '0');

  return { time: `${hh}:${mm}:${ss}`, dayShift };
}

export function utcTimeToLocal(timeStr: string, timezone: string): string {
  return utcTimeToLocalWithDayShift(timeStr, timezone).time;
}

/**
 * Convierte hora UTC a hora local y retorna cuántos días cambia (0, +1, -1)
 * Ej: 00:00 UTC en Argentina (UTC-3) → 21:00 del día anterior, dayShift = -1
 */
export function utcTimeToLocalWithDayShift(timeStr: string, timezone: string): { time: string; dayShift: number } {
  if (!timeStr) return { time: '', dayShift: 0 };

  const timeParts = timeStr.split(':').map(Number);
  const hours = timeParts[0] || 0;
  const minutes = timeParts[1] || 0;
  const seconds = timeParts[2] || 0;

  // Usar día 15 (mitad de mes) para evitar problemas con bordes de mes
  const utcDate = new Date(Date.UTC(2024, 5, 15, hours, minutes, seconds));

  const utcDayFormatter = new Intl.DateTimeFormat('en-US', { timeZone: 'UTC', day: 'numeric' });
  const localDayFormatter = new Intl.DateTimeFormat('en-US', { timeZone: timezone, day: 'numeric' });

  const utcDay = parseInt(utcDayFormatter.format(utcDate));
  const localDay = parseInt(localDayFormatter.format(utcDate));

  const dayShift = localDay - utcDay;

  const formatter = new Intl.DateTimeFormat('en-US', {
    timeZone: timezone,
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  });

  const formattedParts = formatter.formatToParts(utcDate);
  const hour = formattedParts.find(p => p.type === 'hour')?.value || '00';
  const minute = formattedParts.find(p => p.type === 'minute')?.value || '00';
  const second = formattedParts.find(p => p.type === 'second')?.value || '00';

  return { time: `${hour}:${minute}:${second}`, dayShift };
}

